import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Utils from "../../Utils";
import HotelAccessRequest from "./HotelAccessRequest";
import _ from "lodash";

const AccessibleHotelsSection = ({ userData, brandDetails, onHotelSelect }) => {
  const [accessibleHotels, setAccessibleHotels] = useState([]);
  const [accessibleHotelsLoading, setAccessibleHotelsLoading] = useState(false);
  const [hotelAccessModalOpen, setHotelAccessModalOpen] = useState(false);
  const MySwal = withReactContent(Swal);
  const storedUserData = Utils.getUserData() || {};
  const selectedHotelId = Number(storedUserData.hotel_id || 0);
  const selectedHotelName = storedUserData.hotel_name || "";
  //const [isExpanded, setIsExpanded] = useState(!selectedHotelId);
  const [isExpanded, setIsExpanded] = useState(true);

  /*useEffect(() => {
    setIsExpanded(!selectedHotelId);
  }, [selectedHotelId]);
*/
  const fetchAccessibleHotels = () => {
    if (Utils.getUserData()?.isHO) {
      setAccessibleHotelsLoading(true);
      axios
        .get(
          Utils.apiUrl("frontend/auth/hotel-owner/hotels"),
          Utils.apiHeaders(),
        )
        .then((res) => {
          if (!res.data.success) {
            setAccessibleHotels([]);
            return;
          }

          setAccessibleHotels(
            (res.data.data || []).map((hotel) => ({
              ...hotel,
              has_access: true,
            })),
          );
        })
        .catch(() => {
          setAccessibleHotels([]);
        })
        .finally(() => {
          setAccessibleHotelsLoading(false);
        });
      return;
    }

    if (!(Utils.isAMT() || Utils.isHR())) {
      return;
    }

    setAccessibleHotelsLoading(true);
    axios
      .get(Utils.apiUrl("hr/hotel-access/list"), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setAccessibleHotels(res.data.data || []);
        }
      })
      .catch(() => {})
      .finally(() => {
        setAccessibleHotelsLoading(false);
      });
  };

  useEffect(() => {
    fetchAccessibleHotels();
  }, []);

  const formatAccessSince = (value) => {
    if (!value) {
      return "-";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusTone = (hotel) => {
    if (hotel.has_access && Number(hotel.id) === selectedHotelId) {
      return "bg-green-200";
    }
    if (hotel.has_access) {
      return "bg-green-50 hover:bg-green-100";
    }

    if (hotel.pending_request) {
      return "bg-yellow-50";
    }

    if (hotel.rejected_request) {
      return "bg-red-50";
    }

    return "border-gray-200 bg-white";
  };

  const removePendingRequest = (hotel) => () => {
    if (!hotel.request_id) {
      return;
    }

    MySwal.fire({
      title: "Remove Hotel Access Request?",
      text: `Remove the pending request for ${hotel.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "rounded-xl",
        confirmButton:
          "bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded mx-2",
        cancelButton:
          "bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded mx-2",
      },
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      axios
        .delete(
          Utils.apiUrl(`hr/hotel-access/request/${hotel.request_id}`),
          Utils.apiHeaders(),
        )
        .then((res) => {
          if (res.data.success) {
            MySwal.fire("Removed", "Pending request removed.", "success");
            fetchAccessibleHotels();
          } else {
            MySwal.fire(
              "Error",
              res.data.message || "Failed to remove request.",
              "error",
            );
          }
        })
        .catch(() => {
          MySwal.fire("Error", "Failed to remove request.", "error");
        });
    });
  };

  const selectHotel = (hotel) => () => {
    if (!hotel.has_access || Number(hotel.id) === selectedHotelId) {
      return;
    }

    const hotelLabel = `${hotel.name || "Hotel"}${hotel.marsha ? ` (${hotel.marsha})` : ""}, ${hotel.country_name || "-"}`;

    Utils.addToUserData({
      hotel_id: Number(hotel.id),
      hotel_name: hotelLabel,
      hotel_country_id: Number(hotel.country_id),
    });
    onHotelSelect(hotel.id);
    //window.location.reload();
  };

  const handleHotelCardKeyDown = (hotel) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectHotel(hotel)();
    }
  };

  return (
    <>
      <div className="dashboard-section">
        <button
          type="button"
          className="section-title flex w-full items-center justify-between text-left"
          onClick={() => setIsExpanded((current) => !current)}
          aria-expanded={isExpanded}
        >
          <span>
            <i className="fas fa-building"></i>
            {selectedHotelId &&
            (Utils.isAMT() || Utils.isHR() || Utils.isHotelOwner())
              ? `My Hotels - ${selectedHotelName || "Selected Hotel"}`
              : "My Hotels"}
          </span>
          <i
            className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"} text-sm`}
            aria-hidden="true"
          ></i>
        </button>

        {isExpanded &&
          (Utils.isAMT() || Utils.isHR() || Utils.getUserData()?.isHO) && (
            <>
              {selectedHotelId === 0 &&
                accessibleHotels.length > 0 &&
                !accessibleHotelsLoading && (
                  <div className="mb-4 rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800">
                    Click on one of the hotel cards to start your photoshoot. If
                    you don't see your hotel, you can request access to it
                    below.
                  </div>
                )}

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-4">
                {accessibleHotelsLoading && (
                  <div className="brand-card p-6 text-sm text-gray-500">
                    Loading accessible hotels...
                  </div>
                )}

                {!accessibleHotelsLoading &&
                  accessibleHotels.map((hotel) => (
                    <div
                      key={hotel.id}
                      className={`brand-card border p-6 ${getStatusTone(hotel)} ${
                        hotel.has_access
                          ? "cursor-pointer transition hover:shadow-md"
                          : ""
                      }`}
                      onClick={selectHotel(hotel)}
                      onKeyDown={handleHotelCardKeyDown(hotel)}
                      role={hotel.has_access ? "button" : undefined}
                      tabIndex={hotel.has_access ? 0 : undefined}
                      aria-pressed={
                        hotel.has_access
                          ? Number(hotel.id) === selectedHotelId
                          : undefined
                      }
                    >
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div>
                          <p className="text-xl font-bold text-gray-800">
                            {hotel.name}
                          </p>
                          {hotel.has_access &&
                            Number(hotel.id) === selectedHotelId && (
                              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-blue-600">
                                Selected Hotel
                              </p>
                            )}
                        </div>
                        {hotel.pending_request && (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              removePendingRequest(hotel)();
                            }}
                            className="flex h-9 w-9 items-center justify-center rounded-full text-yellow-700 transition hover:bg-yellow-100 hover:text-red-600"
                            title="Remove pending request"
                            aria-label={`Remove pending request for ${hotel.name}`}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        )}
                      </div>

                      <div className="space-y-3 text-sm text-gray-700">
                        <div>
                          <p className="mt-1 text-base font-semibold text-gray-800">
                            <b>{hotel.marsha || "-"}</b> -{" "}
                            {hotel.brand_name || "-"} ,{" "}
                            {hotel.country_name || "-"}
                          </p>
                        </div>
                        {Utils.getUserData()?.isHO && (
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Admin Assigned
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                {!accessibleHotelsLoading && !Utils.getUserData()?.isHO && (
                  <button
                    type="button"
                    onClick={() => setHotelAccessModalOpen(true)}
                    className="brand-card group flex min-h-[240px] flex-col justify-between border-2 border-dashed border-blue-300 p-6 no-underline transition hover:border-blue-500 hover:bg-blue-50"
                  >
                    <div>
                      {_.get(accessibleHotels, "length", 0) > 0 && (
                        <>
                          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">
                            Need another property?
                          </p>
                          <p className="mt-2 text-2xl font-bold text-gray-800 group-hover:text-blue-700">
                            Request Access to Additional Hotel
                          </p>
                        </>
                      )}
                      {_.get(accessibleHotels, "length", 0) === 0 && (
                        <p className="mt-2 text-2xl font-bold text-gray-800 group-hover:text-blue-700">
                          Request Hotel Access
                        </p>
                      )}

                      <p className="mt-3 text-sm text-gray-600">
                        Search Marriott properties and send a new access
                        request.
                      </p>
                    </div>
                  </button>
                )}
              </div>
            </>
          )}
      </div>

      {(Utils.isAMT() || Utils.isHR()) && (
        <HotelAccessRequest
          userData={userData}
          open={hotelAccessModalOpen}
          onClose={() => setHotelAccessModalOpen(false)}
          onRequestSubmitted={fetchAccessibleHotels}
        />
      )}
    </>
  );
};

export default AccessibleHotelsSection;
